import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import AppRoutes from 'src/features/router/data/app-routes';
import { useEffect } from 'react';
import { VariantType, useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { ErrorStatusCode } from 'src/features/error/ErrorStatusCode';
import FormContainer from './form-container';
import { clientsActions, clientsSelectors } from '../../redux/clients-slice';
import useClientApi from '../../hooks/use-client-api';
import { Client } from '../../client-data-types';
import LazyLoadedClientHistory from '../history/lazy-loaded-client-history';

const ClientComponent: React.FC = () => {
    const selectedClient = useAppSelector(clientsSelectors.selectedClient);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { clientId } = useParams();
    const { getClient, updateClient, deleteClient } = useClientApi();

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    if (clientId === undefined) {
        return <Navigate to={AppRoutes.rootPath} />;
    }

    useEffect(() => {
        if (selectedClient === null) {
            getClient(clientId).then((client) => {
                if (client.response === null) {
                    if (client.errorStatusCode) {
                        showMessage(t(ErrorStatusCode[+client.errorStatusCode!]), 'error');
                    }
                    navigate(AppRoutes.rootPath);
                    return;
                }
                dispatch(clientsActions.setSelectedClient(client.response));
            });
        }
    }, [clientId]);

    const handleUpdateClient = async (newClientData: Client, successMessage: string) => {
        const updateResult = await updateClient(newClientData);
        if (updateResult.response !== null) {
            dispatch(clientsActions.setSelectedClient(updateResult.response));
            showMessage(successMessage, 'success');
        } else {
            showMessage(t(ErrorStatusCode[+updateResult.errorStatusCode!]), 'error');
        }
    };

    const handleDeleteClient = async () => {
        if (!confirm(t('areYouSure'))) {
            return;
        }
        const clientDeleted = await deleteClient(selectedClient!.id);
        if (clientDeleted.response) {
            showMessage(t('clientDeleted'), 'success');
            navigate(AppRoutes.rootPath);
        } else {
            showMessage(t(ErrorStatusCode[+clientDeleted.errorStatusCode!]), 'error');
        }
    };

    const showMessage = (message: string, variant: VariantType) => {
        enqueueSnackbar(message, {
            variant: variant,
            style: { whiteSpace: 'pre-line' },
        });
    };

    return selectedClient === null ? (
        <></>
    ) : (
        <>
            <FormContainer
                title={`${t('client')} ${selectedClient.id}`}
                client={selectedClient}
                bottomButtonText={t('deleteClient')}
                bottomButtonDisabled={false}
                handleMainActionMethod={handleDeleteClient}
                updateClient={handleUpdateClient}
            />
            <div style={{ marginTop: '10px', width: '100%' }}>
                <LazyLoadedClientHistory clientId={selectedClient.id}></LazyLoadedClientHistory>
            </div>
        </>
    );
};

export default ClientComponent;
